// src/components/private/highgarden/groceryLists/GroceryListDetails.js
// This component is used to show the list of items in a grocery list

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { OverlayTrigger, Tooltip, ListGroup, Button, Alert, Form, Container, Col, Row, InputGroup, FormControl, ToggleButton, ToggleButtonGroup, Badge } from 'react-bootstrap';
import PurchasedItems from './PurchasedItems';
import 'bootstrap-icons/font/bootstrap-icons.css';
import ConfirmationModal from '../../common_components/ConfirmationModal';
import GroceryListModal from './GroceryListModal';
import PurchaseDetailsModal from './PurchaseDetailsModal';
import { getItemMeasure } from '../services';


function GroceryListDetails({ list, refreshLists }) {
    const token = localStorage.getItem('token'); // Get the token from local storage

    // State for items
    const [unpurchasedItems, setUnpurchasedItems] = useState([]);
    const [purchasedItems, setPurchasedItems] = useState([]);

    const [newItem, setNewItem] = useState({ name: '', quantity: 1 }); // State for new item
    const [showModal, setShowModal] = useState(false);

    const [showPurchaseDetailModal, setShowPurchaseDetailModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showPurchasedItems, setShowPurchasedItems] = useState(false);
    const [confirmationModalConfig, setConfirmationModalConfig] = useState({
        show: false,
        title: '',
        body: '',
        onConfirm: () => { },
    });

    const listUuid = list?.uuid; // Get the list uuid from the list object

    // Fetch list of items for the specified grocery list
    const fetchListItems = useCallback(async (uuid, purchased) => {
        try {
            const params = { purchased, list_uuid: uuid }; // Fetch items based on purchased status (UUID)
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user-grocery-items/`, {
                params,
                headers: {
                    Authorization: `Token ${token}`,
                },
            });
            if (purchased) {
                setPurchasedItems(response.data.results);
            } else {
                setUnpurchasedItems(response.data.results);
            }
        } catch (error) {
            console.error('Error fetching list items:', error);
        }
    }, [token]);

    // Fetch list items when the list uuid changes
    useEffect(() => {
        if (listUuid) {
            fetchListItems(listUuid, false); // Fetch unpurchased items by default
            setPurchasedItems([]); // Reset purchased items
            setShowPurchasedItems(false); // Reset the state for showing purchased items
        }
    }, [listUuid, fetchListItems]);

    // Handle changes in the new item form
    const handleNewItemChange = (e) => {
        const { name, value } = e.target;
        setNewItem(prev => ({ ...prev, [name]: value }));
    };

    const handleAddNewItem = async (e) => {
        e.preventDefault();
        const apiUrl = `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user-grocery-items/`;
        const itemData = { ...newItem, list_uuid: listUuid };

        try {
            const response = await axios.post(apiUrl, itemData, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            });

            if (response.status === 201) {
                fetchListItems(listUuid, false);
                setNewItem({ name: '', quantity: 1 });
            }
        } catch (error) {
            console.error('Error adding new item:', error);
        }
    };

    const markItemAsPurchased = async (itemId) => {
        const apiUrl = `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user-grocery-items/${itemId}/`;
        const currentDateTime = new Date().toISOString();
        try {
            const response = await axios.patch(apiUrl, { purchased: true, purchased_on: currentDateTime }, {
                headers: { Authorization: `Token ${token}` },
            });
            if (response.status === 200) {
                fetchListItems(listUuid, false);
            }
        } catch (error) {
            console.error('Error marking item as purchased:', error);
        }
    };

    const undoItemPurchase = async (itemId) => {
        const apiUrl = `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user-grocery-items/${itemId}/`;
        try {
            const response = await axios.patch(apiUrl, { purchased: false, purchased_on: null }, {
                headers: { Authorization: `Token ${token}` },
            });
            if (response.status === 200) {
                fetchListItems(listUuid, true); // Refetch items to update the list
            }
        } catch (error) {
            console.error('Error undoing item purchase:', error);
        }
    };

    const handleOpenPurchaseDetailModal = (item) => {
        setSelectedItem(item);
        setShowPurchaseDetailModal(true);
    };

    const deleteItem = async (itemId) => {
        const apiUrl = `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user-grocery-items/${itemId}/`;
        try {
            const response = await axios.delete(apiUrl, {
                headers: { Authorization: `Token ${token}` },
            });
            if (response.status === 204) {
                fetchListItems(listUuid, false);
                handleCloseDeleteConfirmModal();
            }
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    const handleOpenDeleteConfirmModal = (itemId) => {
        setConfirmationModalConfig({
            show: true,
            title: 'Confirm Delete',
            body: 'Are you sure you want to delete this item?',
            onConfirm: () => deleteItem(itemId),
        });
    };

    const handleCloseDeleteConfirmModal = () => {
        setConfirmationModalConfig(prev => ({ ...prev, show: false }));
    };

    const handleCloseEditModal = () => setShowModal(false);

    const togglePurchasedItems = () => {
        if (!showPurchasedItems) {
            fetchListItems(listUuid, true); // Fetch purchased items only when toggled on
        }
        setShowPurchasedItems(!showPurchasedItems);
    };

    return (
        <Container fluid className="px-0">
            <Row>
                <Form onSubmit={handleAddNewItem} style={{ marginTop: '20px', padding: '0' }}>
                    <Row>
                        <Col>
                            <InputGroup className="mb-3">
                                <FormControl
                                    style={{ borderTopLeftRadius: '5px', borderBottomLeftRadius: '5px', marginRight: '0' }}
                                    placeholder="Enter item name"
                                    aria-label="Enter item name"
                                    name="name"
                                    value={newItem.name}
                                    onChange={handleNewItemChange}
                                    required />
                                <FormControl
                                    type="number"
                                    style={{ borderRadius: '0', marginRight: '0', maxWidth: '80px' }}
                                    placeholder="Quantity"
                                    aria-label="Quantity"
                                    name="quantity"
                                    value={newItem.quantity}
                                    onChange={handleNewItemChange}
                                    min="1"
                                    required />
                                <ToggleButtonGroup type="radio" name="quantity-options" className="ml-2">
                                    <ToggleButton
                                        style={{ borderRadius: '0', marginRight: '0', width: '30px' }}
                                        variant="outline-secondary"
                                        onClick={() => setNewItem(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))}
                                    >-</ToggleButton>
                                    <ToggleButton
                                        style={{ borderRadius: '0 5px 5px 0', width: '30px' }}
                                        variant="outline-secondary"
                                        onClick={() => setNewItem(prev => ({ ...prev, quantity: prev.quantity + 1 }))}
                                    >+</ToggleButton>
                                </ToggleButtonGroup>
                                {/* A hidden button to submit the form. User can simply press enter on the keyboard */}
                                <Button type="submit" style={{ display: 'none' }}>Add</Button>
                            </InputGroup>
                        </Col>
                    </Row>
                </Form>
            </Row>
            <Row className="no-gutters">
                <Col style={{padding: '0'}}>
                    {unpurchasedItems.length > 0 ? (
                        <ListGroup variant="flush" style={{ padding: '0' }}>
                            {unpurchasedItems.map(item => (
                                <ListGroup.Item
                                    key={item.id}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleOpenPurchaseDetailModal(item)}
                                >
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <Badge style={{ marginRight: '5px' }}>{item.quantity}x</Badge>
                                            <strong style={{ fontSize: '14px' }}>{item.name}</strong>
                                        </div>
                                        <div className="d-flex ml-auto">
                                            <div>
                                                {item.product &&
                                                    <Badge style={{ marginRight: '10px' }}>{item.linked_product.product_brand.name} - {item.linked_product.name} - {getItemMeasure(item)}</Badge>
                                                }
                                            </div>
                                            <div>
                                                <>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip id="tooltip-top">Mark as purchased</Tooltip>}
                                                    >
                                                        <i className="bi bi-cart-check"
                                                            style={{ marginRight: '10px', cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                markItemAsPurchased(item.id);
                                                            }}
                                                        >
                                                        </i>
                                                    </OverlayTrigger>

                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip id="tooltip-top-trash">Delete item</Tooltip>}
                                                    >
                                                        <i className="bi bi-trash"
                                                            style={{ marginRight: '10px', cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDeleteConfirmModal(item.id)
                                                            }}
                                                        >
                                                        </i>
                                                    </OverlayTrigger>
                                                </>
                                            </div>
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    ) : (
                        <Alert variant="warning">No items found</Alert>
                    )}
                </Col>
            </Row>
            <Row>
                {/* Toggle Button for Purchased Items */}
                <Button onClick={togglePurchasedItems} variant="secondary" style={{ marginTop: '10px' }}>
                    {showPurchasedItems ? 'Hide' : 'Show'} Purchased Items
                </Button>
            </Row>

            {/* Conditionally render PurchasedItems */}
            <Row style={{ marginTop: '5px' }}>
                {showPurchasedItems && <PurchasedItems purchasedItems={purchasedItems} onUndoPurchase={undoItemPurchase} refreshList={() => fetchListItems(listUuid, true)} getItemMeasure={getItemMeasure} />}
            </Row>

            {/* Modal for Purchase Details */}
            {selectedItem && (
                <PurchaseDetailsModal
                    show={showPurchaseDetailModal}
                    onHide={() => setShowPurchaseDetailModal(false)}
                    item={selectedItem}
                    refreshList={() => fetchListItems(listUuid, false)}
                />
            )}

            {/* Confirmation modal */}
            <ConfirmationModal
                show={confirmationModalConfig.show}
                onHide={handleCloseDeleteConfirmModal}
                onConfirm={confirmationModalConfig.onConfirm}
                title={confirmationModalConfig.title}
                body={confirmationModalConfig.body} />

            {/* Grocery List Modal for editing */}
            <GroceryListModal
                show={showModal}
                handleClose={handleCloseEditModal}
                currentList={list}
                refreshLists={refreshLists} />
        </Container>
    );
}

export default GroceryListDetails;